import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, FileText, Download, Gift, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { NavBar, Footer } from "../components/Layout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreePage = () => {
  const [email, setEmail] = useState("");
  const [selectedDoc, setSelectedDoc] = useState("aide-memoire-sn4");
  const [loading, setLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Entre ton adresse email."); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/free-download`, { email, document_id: selectedDoc });
      setDownloadUrl(r.data.download_url);
      toast.success(`${r.data.document_name} prêt à télécharger!`);
      window.open(r.data.download_url, "_blank");
    } catch {
      toast.error("Erreur lors du téléchargement. Réessaie.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <NavBar active="gratuit" />

      {/* Hero */}
      <section className="relative py-16 sm:py-20 overflow-hidden gradient-hero grid-overlay">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Gift className="w-4 h-4" /> Gratuit
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Aide-mémoire <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-300">gratuit</span>
            </h1>
            <p className="text-lg text-blue-200/70 max-w-2xl mx-auto">
              Télécharge gratuitement ton aide-mémoire pour te préparer à ton examen. Choisis entre SN4 ou CST4.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-navy-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-display font-bold text-3xl text-white mb-6">Aide aux devoirs et révision : qu'est-ce qu'un aide-mémoire?</h2>
              <p className="text-blue-200/70 text-lg mb-6">
                Un document compact qui résume les <strong className="text-white">formules essentielles</strong> et les <strong className="text-white">concepts clés</strong> de ton cours de mathématiques au secondaire. Parfait pour la <strong className="text-white">révision</strong>, l'<strong className="text-white">aide aux devoirs</strong> et la <strong className="text-white">préparation à l'examen du ministère</strong>.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  "Résumé des formules essentielles",
                  "Format PDF compact et facile à imprimer",
                  "Téléchargement instantané",
                  "Parfait pour réviser avant l'examen"
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-blue-200/70">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <form onSubmit={handleDownload} className="card-dark glow-blue">
                <h3 className="font-semibold text-xl text-white mb-6">Télécharge ton aide-mémoire</h3>

                {/* Document choice */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-blue-200/70 mb-3">Choisis ton document :</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: "aide-memoire-sn4", label: "SN4", sub: "Maths Sciences Nat." },
                      { id: "aide-memoire-cst4", label: "CST4", sub: "Maths Culture Soc." },
                      { id: "aide-memoire-mecanique", label: "Mécanique", sub: "Physique 203-SN1-Re" }
                    ].map(d => (
                      <button key={d.id} type="button" data-testid={`select-${d.label.toLowerCase()}`}
                        onClick={() => { setSelectedDoc(d.id); setDownloadUrl(null); }}
                        className={`p-4 rounded-xl border-2 text-center transition-all ${
                          selectedDoc === d.id ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-white/10 text-blue-200/60 hover:border-white/20"
                        }`}>
                        <FileText className="w-6 h-6 mx-auto mb-2" />
                        <span className="font-semibold text-sm">{d.label}</span>
                        <p className="text-xs mt-1">{d.sub}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Email */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-blue-200/70 mb-2">Ton adresse email :</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-300/40" />
                    <input type="email" data-testid="free-download-email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder="ton.email@example.com" required
                      className="w-full pl-11 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-white placeholder-blue-300/30" />
                  </div>
                </div>

                {/* Submit */}
                <button type="submit" data-testid="free-download-submit" disabled={loading}
                  className="w-full bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-emerald-500 transition-all flex items-center justify-center gap-2">
                  {loading ? <div className="spinner"></div> : <><Download className="w-5 h-5" /> Télécharger gratuitement</>}
                </button>

                {downloadUrl && (
                  <div className="mt-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                    <p className="text-emerald-400 text-sm mb-2">Ton aide-mémoire est prêt!</p>
                    <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="download-link"
                      className="text-emerald-300 underline text-sm">Cliquer ici si le téléchargement ne s'est pas lancé</a>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Internal links */}
      <section className="py-12 bg-navy-900 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-white font-semibold mb-4">Besoin de plus? Découvre nos cahiers de révision complets</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/sn4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Cahier SN4 - 26,99$</Link>
            <Link to="/cst4" className="text-amber-400 hover:text-amber-300 text-sm font-medium border border-amber-500/20 px-4 py-2 rounded-lg hover:bg-amber-500/10 transition-all">Cahier CST4 - 24,99$</Link>
            <Link to="/contact" className="text-blue-400 hover:text-blue-300 text-sm font-medium border border-blue-500/20 px-4 py-2 rounded-lg hover:bg-blue-500/10 transition-all">Nous contacter</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FreePage;
